import React, { useEffect, useState } from 'react'
import { presenterHub, presenterUser } from '@RestApi/types'
import Avatar from '@Components/Avatar'
import media from '@Styles/media'
import { atLeastModerator } from '@App/roles/utils'
import UserPVField from './UserPVField'
import styled from 'styled-components'
import { rpcApiAtom } from '@Modules/hubs/atoms'
import { useRecoilValue } from 'recoil'
import Spinner from '@Components/Spinner'
import { MemberVariableValue } from '@RestApi/api'
import { HubText } from 'src/common/rtl/HubWidgets'

type Props = {
  user: presenterUser
  hub: presenterHub
  displayMemberIdAndEmail: boolean
}

const UserCard: React.FC<Props> = (props: Props) => {
  const { user, hub, displayMemberIdAndEmail } = props
  const { name, email, memberId, role } = user
  const rpcApi = useRecoilValue(rpcApiAtom)

  const [memberVariableValues, setMemberVariableValues] = useState<
    MemberVariableValue[] | undefined
  >(undefined)
  const [isFetched, setIsFetched] = useState(false)

  const fetchProfileVariables = async (memberId: string, hubId: string) => {
    try {
      const resp = await rpcApi.getMemberVariablesValues({
        hubId,
        memberId,
      })
      setMemberVariableValues(resp.memberVariablesValue)
      setIsFetched(true)
    } catch (e) {
      console.log('error', e)
    }
  }

  useEffect(() => {
    if (memberId) {
      fetchProfileVariables(memberId, hub.id)
    } else {
      setIsFetched(true)
    }
  }, [])

  return (
    <Card>
      {!isFetched ? (
        <StyledSpinner active={true} inline={true} />
      ) : (
        <>
          <Avatar type="user" className="px-posted-by-avatar" account={user} size={55} />
          <UserCardWrapper>
            <UsernameWrapper>
              <Username>{name}</Username>
            </UsernameWrapper>
            {memberId && memberVariableValues ? (
              <Content>
                {(displayMemberIdAndEmail || atLeastModerator({ role: hub.role })) && (
                  <MemberIdAndEmailField email={email} memberId={memberId} />
                )}
                <UserPVField fieldName={'Role'} fieldValue={role || 'N/A'} />
                {memberVariableValues.map((PV, index) => {
                  return (
                    <UserPVField
                      key={index}
                      fieldName={PV.name || PV.sparq_name}
                      fieldValue={PV.value}
                    />
                  )
                })}
              </Content>
            ) : (
              <Content>
                {(displayMemberIdAndEmail || atLeastModerator({ role: hub.role })) && (
                  <MemberIdAndEmailField email={email} memberId={memberId} />
                )}
                <UserPVField fieldName={'Role'} fieldValue={role || 'N/A'} />
              </Content>
            )}
          </UserCardWrapper>
        </>
      )}
    </Card>
  )
}

const MemberIdAndEmailField = ({
  email,
  memberId,
}: {
  email: string
  memberId: string | undefined
}) => {
  return (
    <>
      <UserPVField fieldName={'Email'} fieldValue={email} />
      <UserPVField fieldName={'MemberId'} fieldValue={memberId || 'N/A'} />
    </>
  )
}

const StyledSpinner = styled(Spinner)`
  position: absolute !important;
  align-self: center;
  justify-content: center;
`

const Content = styled.div`
  max-height: 150px;
  min-height: auto;
  width: auto;
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`

const Card = styled.div`
  background-color: rgb(255, 255, 255);
  padding: 10px 0px 10px 16px;
  max-width: 712px;
  min-width: 300px;
  max-height: 188px;
  min-height: 80px;
  position: relative;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: top;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 18px 0px;

  ${media.smallScreen`
    width: 360px;
    padding: 10px 0px 10px 10px;
  `};
`

const UserCardWrapper = styled.div`
  max-width: 360px;
  min-width: 300px;
  width: auto;
  height: auto;
  display: flex;
  justify-content: space-around;
  padding: 0px 0px 3px 12px;
  flex-direction: column;

  ${media.smallScreen`
    display: block;
    padding-bottom: 4px;
    padding-right: 0;
  `}
`

const UsernameWrapper = styled.div`
  display: flex;
  padding-right: 10px;
`

const Username = styled(HubText)`
  width: 100%;
  width: fit-content;
  padding-right: 8px;
  font-style: normal;
  font-weight: 900;
  font-size: 18px;
  line-height: 1.5;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`

export default UserCard
